/**
 * Trust Center Page — M6.2 Trust & Compliance
 * Public bilingual (EN/PT-BR) trust and compliance transparency page
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Shield, BookOpen, FileText, Globe, Lock, Database, Bot, Server,
  ArrowRight, CheckCircle2, ExternalLink, Mail, MapPin, Building2,
  ShieldCheck, Eye, Layers, Bell, ChevronRight
} from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n/config';
import PublicLanguageSwitcher from '../components/PublicLanguageSwitcher';

const SECTION_ICONS = {
  trust_principles: CheckCircle2,
  standards_alignment: BookOpen,
  soa: Layers,
  ai_governance: Bot,
  data_integrity: Database,
  privacy: Lock,
  security_resilience: ShieldCheck,
  governance_docs: FileText,
  regional_readiness: Globe,
  onboarding: Building2,
  support: Mail,
};

const TrustSection = ({ sectionKey, title, body, bullets, note, cta, links, t }) => {
  const Icon = SECTION_ICONS[sectionKey] || Shield;

  return (
    <section
      className="bg-[#111827] border border-[rgba(255,255,255,0.06)] rounded-xl p-6 hover:border-[rgba(54,169,225,0.2)] transition-colors"
      data-testid={`trust-section-${sectionKey}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-lg bg-[rgba(54,169,225,0.1)] shrink-0">
          <Icon className="text-[#36A9E1]" size={20} />
        </div>
        <h2 className="text-white font-semibold text-lg leading-tight pt-1">{title}</h2>
      </div>

      {body && <p className="text-[#8B95A5] text-sm leading-relaxed mb-4">{body}</p>}

      {bullets && bullets.length > 0 && (
        <ul className="space-y-2 mb-4">
          {bullets.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-[#B8C7D9]">
              <ChevronRight className="text-[#36A9E1] shrink-0 mt-0.5" size={14} />
              {item}
            </li>
          ))}
        </ul>
      )}

      {note && (
        <div className="bg-[rgba(217,119,6,0.08)] border-l-3 border-amber-500 rounded-r-lg p-3 mb-4"
             style={{ borderLeft: '3px solid rgb(217 119 6)' }}>
          <p className="text-amber-300 text-xs leading-relaxed">{note}</p>
        </div>
      )}

      {cta && (
        <Link
          to={cta.href}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(54,169,225,0.1)] text-[#36A9E1] text-sm font-medium hover:bg-[rgba(54,169,225,0.2)] transition-colors"
          data-testid={`trust-cta-${sectionKey}`}
        >
          {cta.label}
          <ArrowRight size={14} />
        </Link>
      )}

      {links && links.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-2">
          {links.map((link, idx) => (
            <Link
              key={idx}
              to={link.href}
              className="inline-flex items-center gap-1 text-[#36A9E1] text-sm hover:text-[#38BDF8] transition-colors"
            >
              {link.label}
              <ExternalLink size={12} />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

const TrustCenterPage = () => {
  const { t, i18n } = useTranslation('trust');

  const sectionKeys = [
    'trust_principles',
    'standards_alignment',
    'soa',
    'ai_governance',
    'data_integrity',
    'privacy',
    'security_resilience',
    'governance_docs',
    'regional_readiness',
    'onboarding',
    'support',
  ];

  const changeLang = (code) => {
    i18n.changeLanguage(code);
  };

  return (
    <main className="min-h-screen bg-[#0B0E14]" data-testid="trust-center-page">
      {/* Top Nav Bar */}
      <nav className="border-b border-[rgba(255,255,255,0.06)] bg-[#0B0E14]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="trust-nav-logo">
            <Shield className="text-[#36A9E1]" size={24} />
            <span className="font-['Syne'] font-bold text-white text-lg">CHARSTAN</span>
          </Link>
          <div className="flex items-center gap-4">
            <PublicLanguageSwitcher />
            <Link
              to="/login"
              className="px-3 py-1.5 text-xs font-medium text-[#36A9E1] border border-[#36A9E1]/30 rounded hover:bg-[#36A9E1]/10 transition-colors"
              data-testid="trust-login-link"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl mb-10" data-testid="trust-hero">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f1c2e] via-[#112240] to-[#0f1629]" />
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(54,169,225,0.08) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(54,169,225,0.05) 0%, transparent 50%)',
          }} />
          <div className="relative p-8 md:p-12">
            <p className="uppercase tracking-[0.25em] text-xs text-[#36A9E1] font-medium mb-4" data-testid="trust-page-title">
              {t('pageTitle')}
            </p>
            <h1 className="font-['Syne'] text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-4xl" data-testid="trust-hero-title">
              {t('hero.title')}
            </h1>
            <p className="text-base md:text-lg text-[#B8C7D9] mb-4 max-w-3xl">
              {t('hero.subtitle')}
            </p>
            <p className="text-sm text-[#8B95A5] leading-relaxed mb-8 max-w-4xl">
              {t('hero.description')}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/standards"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#36A9E1] text-white text-sm font-semibold hover:bg-[#2d94c8] transition-colors"
                data-testid="trust-cta-standards"
              >
                {t('hero.ctas.standards')}
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/soa"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[rgba(255,255,255,0.06)] text-white text-sm font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors border border-[rgba(255,255,255,0.1)]"
                data-testid="trust-cta-soa"
              >
                {t('hero.ctas.soa')}
              </Link>
              <Link
                to="/governance"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[rgba(255,255,255,0.06)] text-white text-sm font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors border border-[rgba(255,255,255,0.1)]"
                data-testid="trust-cta-governance"
              >
                {t('hero.ctas.governance')}
              </Link>
            </div>
          </div>
        </section>

        {/* Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10" data-testid="trust-sections-grid">
          {sectionKeys.map((key) => {
            const bullets = t(`sections.${key}.bullets`, { returnObjects: true, defaultValue: [] });
            const links = t(`sections.${key}.links`, { returnObjects: true, defaultValue: [] });
            const ctaLabel = t(`sections.${key}.cta.label`, { defaultValue: '' });
            const ctaHref = t(`sections.${key}.cta.href`, { defaultValue: '' });
            const noteText = t(`sections.${key}.note`, { defaultValue: '' });

            // Filter out raw i18n key paths (when key doesn't exist in translation)
            const isValidTranslation = (val) => val && !val.startsWith('sections.');

            return (
              <TrustSection
                key={key}
                sectionKey={key}
                title={t(`sections.${key}.title`)}
                body={t(`sections.${key}.body`, { defaultValue: '' })}
                bullets={Array.isArray(bullets) ? bullets : []}
                note={isValidTranslation(noteText) ? noteText : null}
                cta={isValidTranslation(ctaLabel) && isValidTranslation(ctaHref) ? { label: ctaLabel, href: ctaHref } : null}
                links={Array.isArray(links) ? links : []}
                t={t}
              />
            );
          })}
        </div>

        {/* Contact + Footer */}
        <footer className="border-t border-[rgba(255,255,255,0.06)] pt-8 pb-6" data-testid="trust-footer">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="text-[#36A9E1]" size={16} />
                <h3 className="text-white text-sm font-semibold">Locations</h3>
              </div>
              <p className="text-[#8B95A5] text-xs leading-relaxed">{t('footer.hq')}</p>
              <p className="text-[#8B95A5] text-xs leading-relaxed mt-1">{t('footer.br')}</p>
              <p className="text-[#8B95A5] text-xs leading-relaxed mt-1">{t('footer.cnpj')}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Mail className="text-[#36A9E1]" size={16} />
                <h3 className="text-white text-sm font-semibold">Contact</h3>
              </div>
              <p className="text-[#8B95A5] text-xs">{t('sections.support.contact_general')}</p>
              <p className="text-[#8B95A5] text-xs mt-1">{t('sections.support.contact_support')}</p>
              <p className="text-[#8B95A5] text-xs mt-1">{t('sections.support.contact_automated')}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="text-[#36A9E1]" size={16} />
                <h3 className="text-white text-sm font-semibold">Governance</h3>
              </div>
              <p className="text-[#8B95A5] text-xs leading-relaxed">{t('footer.governance_note')}</p>
            </div>
          </div>

          <div className="border-t border-[rgba(255,255,255,0.04)] pt-4">
            <p className="text-[#6B7280] text-xs text-center leading-relaxed">
              {t('footer.disclaimer')}
            </p>
          </div>
        </footer>
      </div>
    </main>
  );
};

export default TrustCenterPage;
